import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TableFooter,
  Typography,
  Box,
  Modal,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import EditModalForHoldings from "../Modal/EditModalForPrivateStockHoldings";
import {
  HoldingsPageShell,
  HoldingsAddButton,
  SortableHeader,
  EmptyTableRow,
  centerBodyCellSx,
  centerHeadCellSx,
  gainLossCellSx,
  holdingsPaginationSx,
} from "./holdingsTableShared";

interface Unlisted {
  requestId: number;
  id: number;
  noteficationId: number;
  holdingName: string;
  category: string;
  investedAmount: number;
  rateDate: string;
  rate: number;
  realisedPnL: number;
  transactions: [];
  days: number;
  unrealisedPnL: number;
  absolutePnL: number;
  type:string;
  symbol: string;
  units: number;
  amount: number;
  purchasePrice: number;
  currentAmount: number;
  redeemUnits: number;
  transactionDate: string;
  currentPrice: number;
  gainLoss: number;
  gainLossPerc: string;
  xirr:string;
}

interface Props {
  data: Unlisted[];
  totalPages: number; // Total number of users
  pageSize: number; // Current page size
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void; // Page change handler
  onPageSizeChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void; // Page size change handler
  currentPage: number; // Current page number
  fetchData: () => void;
}

interface TransactionsModalProps {
  open: boolean;
  onClose: () => void;
  transactions: {
    transactionDate: string;
    investedAmount: number;
    days: number;
  }[];
}
const TransactionsModal: React.FC<TransactionsModalProps> = ({
  open,
  onClose,
  transactions,
}) => {
  const formatTransactionDate = (dateString: any) => {
    if (dateString.includes("/")) {
      const [day, month, year] = dateString.split("/"); // Convert MM/DD/YYYY format to DD-MM-YYYY
      return `${day}-${month}-${year}`;
    }

    // Check if the date is already in DD-MM-YYYY format
    if (dateString.includes("-")) {
      const [day, month, year] = dateString.split("-");
      return `${day}-${month}-${year}`; // Keep as DD-MM-YYYY
    }

    return dateString; // If the format is unexpected, return the original
  };

  const calculateDays = (dateString: any) => {
    // Convert formatted date string to Date object
    const [day, month, year] = dateString.split("-");
    const transactionDate = new Date(`${year}-${month}-${day}T00:00:00`);
    const today = new Date();

    // Reset the time portion to ignore time differences
    const todayWithoutTime = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    // Calculate the difference in milliseconds
    const timeDiff = todayWithoutTime.getTime() - transactionDate.getTime();
    // Convert milliseconds to days
    const diffDays = Math.floor(timeDiff / (1000 * 3600 * 24));

    return isNaN(diffDays) ? 1 : diffDays + 1; // Return 0 days for the same day or NaN case
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800, // Adjust width as needed
          maxHeight: "80vh", // Limit the height of the modal
          bgcolor: "background.paper",
          border: "2px solid #000",
          boxShadow: 24,
          p: 4,
          overflowX: "auto", // Make the content scrollable on the Y-axis
          borderRadius: 2,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Transaction Details</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "200px",
            borderTop: "1.5px solid rgba(224, 224, 224, 1)",
            padding: "0 20px",
            overflow: "auto", // Enable horizontal scrolling
            maxWidth: "100%", // Ensure the table doesn't exceed the container's width
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "start" }}>
                  Purchase Date
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                  Invested Amount
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                  Withdrawal Amount
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "end" }}>
                  Days
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions?.map((trans: any, index: any) => (
                <TableRow key={index}>
                  <TableCell sx={{ textAlign: "start" }}>
                    {formatTransactionDate(trans?.transactionDate)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {trans?.investedAmount}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {Number(trans?.redeemValue).toFixed(2) || 0}
                  </TableCell>
                  <TableCell sx={{ textAlign: "end" }}>
                    {calculateDays(
                      formatTransactionDate(trans?.transactionDate)
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Modal>
  );
};
const UnlistedHoldingsTable: React.FC<Props> = ({
  data,
  totalPages,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
  fetchData,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState<Unlisted[]>([]);
  const [loadingRows, setLoadingRows] = useState<number[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Unlisted | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [transModalOpen, setTransModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Unlisted | null>(null);
  const [modalMode, setModalMode] = useState<"edit" | "add" | "plus"|"AddButton">("edit");
  const [selectedTransactions, setSelectedTransactions] = useState<[] | null>(
    null
  );

  console.log("this is current page", currentPage);
  console.log("this is page size", pageSize);

  const isEmptyObject = (obj: object): boolean => {
    return Object.keys(obj).length === 0;
  };

  useEffect(() => {
    // Retrieve selectedData from session storage if available
    const storedData = sessionStorage.getItem("selectedData");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (!isEmptyObject(parsedData)) {
        setSelectedRow(parsedData); // Set selected data row
        setModalMode("plus"); // Set the mode to 'plus'
        setModalOpen(true); // Open the modal
      } else {
        setModalOpen(false);
        setSelectedRow(null);
      }
    }
  }, []);

    useEffect(() => {
      // Filter and sort data whenever the data or search term changes
      let updatedData = data;
  
      // Search functionality
      if (searchTerm) {
        updatedData = updatedData?.filter((row) =>
          Object.values(row).some(
            (value) =>
              value != null &&
              value.toString().toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
      }
  
      // Sorting functionality
      if (sortColumn) {
        updatedData = [...updatedData].sort((a, b) => {
          if (a[sortColumn] < b[sortColumn]) return sortOrder === "asc" ? -1 : 1;
          if (a[sortColumn] > b[sortColumn]) return sortOrder === "asc" ? 1 : -1;
          return 0;
        });
      }
  
      setFilteredData(updatedData);
    }, [data, searchTerm, sortColumn, sortOrder]);

  const handleSort = (column: keyof Unlisted) => {
    setSortColumn(column);
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleModalClose = () => {
    sessionStorage.removeItem("selectedData");
    setSelectedRow(null);
    setModalOpen(false);
  };
  const handleCloseModal = () => {
    setTransModalOpen(false);
    setSelectedTransactions(null);
  };
  const handleModalSubmit = (data: Unlisted) => {
    handleModalClose();
  };

  const handleOpenModal = (transactions: any) => {
    setSelectedTransactions(transactions);
    setTransModalOpen(true);
  };

  const handleAddNew = () => {
    setSelectedRow(null);
    setModalMode("AddButton");
    setModalOpen(true);
  };

  const pagedData = filteredData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ?? [];

  const sortHead = (label: string, column: keyof Unlisted) => (
    <TableCell sx={centerHeadCellSx}>
      <SortableHeader label={label} onSort={() => handleSort(column)} align="center" />
    </TableCell>
  );

  return (
    <HoldingsPageShell
      title="Private Stock"
      subtitle="View private stock holdings for this investor."
      searchValue={searchTerm}
      onSearchChange={(value) => {
        setSearchTerm(value);
        setPage(0);
      }}
      searchPlaceholder="Search private stock holdings..."
      action={<HoldingsAddButton onClick={handleAddNew} />}
    >
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {sortHead("Scheme", "symbol")}
              {sortHead("Quantity", "units")}
              {sortHead("Purchase Price", "purchasePrice")}
              {sortHead("Current Price", "currentPrice")}
              <TableCell sx={centerHeadCellSx}>Current Value</TableCell>
              {sortHead("P & L", "gainLossPerc")}
              {sortHead("XIRR", "xirr")}
            </TableRow>
          </TableHead>

          <TableBody>
            {pagedData.length === 0 ? (
              <TableRow>
                <EmptyTableRow colSpan={7} message="No private stock holdings found" />
              </TableRow>
            ) : (
              pagedData.map((row) => (
                <TableRow key={row.id} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                  <TableCell sx={centerBodyCellSx}>{row?.symbol}</TableCell>
                  <TableCell sx={centerBodyCellSx}>{Number(row?.units).toFixed(2)}</TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {Number(row?.purchasePrice).toFixed(2)}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {Number(row?.currentPrice).toFixed(2)}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {Number(row?.currentAmount).toFixed(2)}
                  </TableCell>
                  <TableCell sx={gainLossCellSx(row?.gainLossPerc)}>{row?.gainLossPerc}</TableCell>
                  <TableCell sx={gainLossCellSx(row?.xirr)}>{row?.xirr ? row?.xirr : "-"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={filteredData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onPageSizeChange}
                sx={holdingsPaginationSx}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <EditModalForHoldings
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        rowData={
          selectedRow || {
            requestId: 0,
            id: 0,
            noteficationId: 0,
            holdingName: "",
            category:"",
            investedAmount: 0,
            rateDate:"",
            rate:0,
            realisedPnL: 0,
            transactions: [],
            days: 0,
            unrealisedPnL: 0,
            absolutePnL: 0,
            type:"",
            symbol: "",
            units:0,
            amount: 0,
            purchasePrice: 0,
            currentAmount: 0,
            redeemUnits: 0,
            transactionDate: "",
            currentPrice: 0,
            gainLoss: 0,
            gainLossPerc: "",
            xirr:""
          }
        }
        filteredData={filteredData}
        mode={modalMode}
        fetchData={fetchData}
      />
      {selectedTransactions && (
        <TransactionsModal
          open={transModalOpen}
          onClose={handleCloseModal}
          transactions={selectedTransactions}
        />
      )}
    </HoldingsPageShell>
  );
};

export default UnlistedHoldingsTable;
